import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../Auth/AuthContext";
import api from "../../services/api";
import { MODULE_ACCESS_ERRORS } from "../../helpers/moduleAccess";

export const ModuleAccessContext = createContext({ features: {}, loading: true, error: false, refresh: () => {} });
export const useModuleAccess = () => useContext(ModuleAccessContext);

export const ModuleAccessProvider = ({ children }) => {
  const { user, isAuth, socket } = useContext(AuthContext);
  const identity = `${user?.companyId}:${user?.id}:${user?.profile}:${user?.allowKanban}:${user?.allowCrm}`;
  const [state, setState] = useState({ identity: null, features: {}, loading: true, error: false });
  const requestId = useRef(0);

  const refresh = useCallback(async ({ invalidate = false } = {}) => {
    const currentRequest = ++requestId.current;
    if (!isAuth || !user?.id || !user?.companyId) return;
    setState(current => ({ ...current, loading: invalidate || current.identity !== identity, error: false }));
    try {
      const { data } = await api.get("/me/features");
      if (currentRequest !== requestId.current) return;
      if (Number(data.companyId) !== Number(user.companyId) || !data.features?.kanban || !data.features?.crm || !data.features?.ai) {
        throw new Error("Invalid module access response");
      }
      setState({ identity, features: data.features, loading: false, error: false });
    } catch (_) {
      if (currentRequest === requestId.current) {
        setState({ identity, features: {}, loading: false, error: true });
      }
    }
  }, [identity, isAuth, user?.id, user?.companyId]);

  useEffect(() => {
    refresh({ invalidate: true });
    return () => { requestId.current += 1; };
  }, [refresh]);

  useEffect(() => {
    if (!isAuth || !user?.companyId || !user?.id) return;
    const eventName = `company-${user.companyId}-module-access`;
    const onChange = data => {
      if (data?.version !== 1 || Number(data.companyId) !== Number(user.companyId)) return;
      if (data.userId !== undefined && Number(data.userId) !== Number(user.id)) return;
      refresh({ invalidate: true });
    };
    const onResume = () => { if (document.visibilityState !== "hidden") refresh(); };
    const onConnect = () => refresh();
    socket?.on?.(eventName, onChange);
    socket?.on?.("connect", onConnect);
    window.addEventListener("focus", onResume);
    window.addEventListener("online", onResume);
    document.addEventListener("visibilitychange", onResume);
    // Covers notifications missed while the transport is unavailable.
    const timer = window.setInterval(onResume, 60000);
    const interceptor = api.interceptors.response.use(response => response, error => {
      if (error.config?.url !== "/me/features" && MODULE_ACCESS_ERRORS.includes(error.response?.data?.error)) {
        refresh({ invalidate: true });
      }
      return Promise.reject(error);
    });
    return () => {
      socket?.off?.(eventName, onChange);
      socket?.off?.("connect", onConnect);
      window.removeEventListener("focus", onResume);
      window.removeEventListener("online", onResume);
      document.removeEventListener("visibilitychange", onResume);
      window.clearInterval(timer);
      api.interceptors.response.eject(interceptor);
    };
  }, [isAuth, user?.companyId, user?.id, socket, refresh]);

  const currentIdentity = isAuth && state.identity === identity;
  return (
    <ModuleAccessContext.Provider value={{
      features: currentIdentity ? state.features : {},
      loading: isAuth && (!currentIdentity || state.loading),
      error: currentIdentity && state.error,
      refresh,
    }}>
      {children}
    </ModuleAccessContext.Provider>
  );
};
